import type React from "react";
import { useCallback, useEffect } from "react";

import { castingSessionIdSelector } from "@redux/casting";
import {
	useCurrentSongSelector,
	initCastingSession,
	useNextSongSelector,
} from "@redux/player";
import { useSelector, useDispatch } from "react-redux";
import { useAsyncFn } from "react-use";

import ControlBarEventsWrapper from "components/shared/ControlBarEventsWrapper";
import { useCastingListener } from "services/hooks/useCastingListener";
import { postCastingEvent } from "services/networking/casting";
import { getSong } from "services/networking/songs";
import { PlayerEvent, type SendPlayerEvent } from "shared-types/player-event";

import CastingControlBarContent from "./components/CastingControlBarContent";

const CastingControlBar: React.FC<{ stripeId: string | null }> = ({
	stripeId,
}) => {
	const [songDetails, fetchSongDetails] = useAsyncFn(getSong, [], {
		loading: true,
	});
	const [, sendCastingEvent] = useAsyncFn(postCastingEvent);
	const castingSessionId = useSelector(castingSessionIdSelector);
	const currentSong = useCurrentSongSelector();
	const nextSong = useNextSongSelector();
	const dispatch = useDispatch();

	useEffect(() => {
		currentSong && fetchSongDetails(currentSong.id);
	}, [currentSong, fetchSongDetails]);

	useEffect(() => {
		if (castingSessionId) {
			sendCastingEvent(
				castingSessionId,
				PlayerEvent.STRIPE_ID,
				stripeId || undefined,
			);
		}
	}, [castingSessionId, stripeId, sendCastingEvent]);

	useCastingListener(
		{
			[PlayerEvent.INIT_CASTING_SESSION]: (event: MessageEvent) => {
				dispatch(initCastingSession(event.data));
			},
		},
		castingSessionId,
	);

	const sendPlayerEvent = useCallback<SendPlayerEvent>(
		(eventType, data) => {
			if (castingSessionId) {
				postCastingEvent(castingSessionId, eventType, data);
			}
		},
		[castingSessionId],
	);

	if (!castingSessionId || !currentSong) {
		return null;
	}

	return (
		<ControlBarEventsWrapper
			ControlBarContentComponent={CastingControlBarContent}
			sendPlayerEvent={sendPlayerEvent}
			usePlayerEventsListener={useCastingListener}
			currentSong={currentSong}
			nextSong={nextSong}
			castingSessionId={castingSessionId}
			songTitle={songDetails.value?.title}
			songArtistName={songDetails.value?.artist.name}
		/>
	);
};

export default CastingControlBar;
